import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity, AlertTriangle, BookOpen, Brain, ChevronRight,
  Download, ExternalLink, FileText, Fingerprint, GitBranch,
  History, Map, MessageSquare,
  Presentation,
  Shield, Target, TrendingDown,
  TrendingUp, Users, X, Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePesa } from '../../stores/usePesaEngine';
import { exportPdf, exportPptx } from './PesaExport';


const TONE = {
  radieux:   { accent: '#22d3ee', label: 'Radieux',   Icon: TrendingUp    },
  lucide:    { accent: '#f59e0b', label: 'Lucide',    Icon: Target        },
  tragique:  { accent: '#f87171', label: 'Tragique',  Icon: TrendingDown  },
  cynique:   { accent: '#a78bfa', label: 'Cynique',   Icon: AlertTriangle },
  désespéré: { accent: '#94a3b8', label: 'Désespéré', Icon: Shield        },
  esperant:  { accent: '#34d399', label: 'Espérant',  Icon: Zap           },
};

const LEVEL = {
  débutant:      { color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', label: 'Débutant'      },
  intermédiaire: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',  label: 'Intermédiaire' },
  avancé:        { color: '#34d399', bg: 'rgba(52,211,153,0.08)',  label: 'Avancé'        },
  expert:        { color: '#22d3ee', bg: 'rgba(34,211,238,0.08)',  label: 'Expert'        },
};

const PRIORITY_ACCENT = ['', '#f87171', '#f59e0b', '#34d399', '#94a3b8'];

const Divider = () => <div className="border-t border-[rgba(255,255,255,0.06)] my-8" />;

const Tag = ({ children, color = '#f59e0b' }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest"
    style={{ color, backgroundColor: `${color}14`, border: `1px solid ${color}28` }}>
    {children}
  </span>
);

const Stat = ({ label, value, sub, accent = '#f59e0b' }) => (
  <div className="space-y-1">
    <p className="text-[7px] font-black uppercase tracking-[0.2em] text-white/25">{label}</p>
    <p className="text-3xl font-black tabular-nums leading-none" style={{ color: accent }}>{value}</p>
    {sub && <p className="text-[8px] text-white/25 italic">{sub}</p>}
  </div>
);

const SectionHead = ({ icon: Icon, label, count }) => (
  <div className="flex items-center gap-3 mb-6">
    <Icon size={12} className="shrink-0" style={{ color: 'rgba(255, 255, 255, 0.15)' }} />  {/* ← 0.25 → 0.15 */}
    <h2 className="text-[8px] font-black uppercase tracking-[0.25em]" style={{ color: 'rgba(255, 255, 255, 0.25)' }}>  {/* ← 0.35 → 0.25 */}
      {label}
    </h2>
    {count != null && (
      <span className="text-[7px] font-mono px-1.5 py-0.5 rounded" style={{
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        color: 'rgba(255, 255, 255, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.04)'
      }}>
        {count}
      </span>
    )}
    <div className="h-px flex-1" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }} />  {/* ← 0.05 → 0.03 */}
  </div>
);

const MetricBar = ({ value }) => {
  const v = Math.max(0, Math.min(100, value ?? 0));
  const c = v >= 70 ? '#34d399' : v >= 40 ? '#f59e0b' : '#f87171';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] font-mono tabular-nums w-7 text-right" style={{ color: c }}>{v}</span>
      <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${v}%`, backgroundColor: c }} />
      </div>
    </div>
  );
};


const SECTIONS = [
  { id: 'synthese',    label: 'Synthèse',       Icon: Zap           },
  { id: 'systeme',     label: 'Système',         Icon: Activity      },
  { id: 'trajectoire', label: 'Trajectoire',     Icon: GitBranch     },
  { id: 'etapes',      label: 'Étapes',          Icon: History       },
  { id: 'agents',      label: 'Agents',          Icon: Users         },
  { id: 'theorie',     label: 'Théorie',         Icon: BookOpen      },
  { id: 'competences', label: 'Compétences',     Icon: Target        },
  { id: 'transfert',   label: 'Transférabilité', Icon: Map           },
  { id: 'debat',       label: 'Débat & Sources', Icon: MessageSquare },
];


const SectionSynthese = ({ sol, interp, tone }) => {
  const { Icon } = tone;
  const gap = Math.abs((interp.theoreticalPromise ?? 0) - (interp.realWorldProbability ?? 0)).toFixed(0);

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="space-y-4 pl-6" style={{ borderLeft: `3px solid ${tone.accent}` }}>
        <div className="flex items-center gap-3">
          <Icon size={14} style={{ color: tone.accent }} />
          <Tag color={tone.accent}>{tone.label}</Tag>
        </div>
        <h1 className="text-5xl font-black uppercase tracking-tighter leading-[0.9] text-white">
          {sol.name || 'ANALYSE TERMINÉE'}
        </h1>
        {interp.shortSummary && (
          <p className="text-xl font-light italic leading-relaxed max-w-2xl" style={{ color: `${tone.accent}bb` }}>
            "{interp.shortSummary}"
          </p>
        )}
      </div>

      {/* Probabilités */}
      <div className="grid grid-cols-3 gap-8 p-8 rounded-xl bg-white/[0.02] border border-[rgba(255,255,255,0.06)]">
        <Stat label="Optimum théorique" value={`${interp.theoreticalPromise ?? '--'}%`} sub="Optimisation génétique" accent="#ffffff" />
        <div className="text-center space-y-1">
          <p className="text-[7px] font-black uppercase tracking-[0.2em] text-white/25">Écart de reproductibilité</p>
          <p className="text-4xl font-black tabular-nums" style={{ color: parseInt(gap) < 30 ? '#34d399' : '#f87171' }}>−{gap}%</p>
        </div>
        <div className="text-right">
          <Stat label="Probabilité réelle (1900–2025)" value={`${interp.realWorldProbability ?? '--'}%`} sub="Corpus historique calibré" accent={tone.accent} />
        </div>
      </div>

      {/* Messages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {interp.keyInsight && (
          <div className="p-6 rounded-xl border border-amber-500/15 bg-amber-500/5 space-y-3">
            <p className="text-[7px] font-black uppercase tracking-widest text-amber-400/50 flex items-center gap-1.5">
              <Zap size={8} /> Insight moteur
            </p>
            <p className="text-sm leading-relaxed text-white/75 italic">"{interp.keyInsight}"</p>
          </div>
        )}
        {interp.pedagogicalMessage && (
          <div className="p-6 rounded-xl border border-[rgba(255,255,255,0.06)] bg-white/[0.02] space-y-3">
            <p className="text-[7px] font-black uppercase tracking-widest text-white/20 flex items-center gap-1.5">
              <Brain size={8} /> Lecture pédagogique
            </p>
            <p className="text-sm leading-relaxed text-white/65 font-light">{interp.pedagogicalMessage}</p>
          </div>
        )}
      </div>

      {interp.counterArgument && (
        <div className="p-6 rounded-xl border-2 border-red-500/20 bg-red-500/5">
          <p className="text-[7px] font-black uppercase tracking-widest text-red-400/60 flex items-center gap-1.5 mb-3">
            <AlertTriangle size={8} /> Angle mort / Risque critique
          </p>
          <p className="text-sm text-white/75 leading-relaxed">{interp.counterArgument}</p>
        </div>
      )}

      {sol.description && (
        <div className="space-y-2 px-1">
          <p className="text-[7px] font-black uppercase tracking-widest text-white/20">Description de la solution</p>
          <p className="text-sm text-white/50 leading-relaxed">{sol.description}</p>
        </div>
      )}
    </div>
  );
};


const SectionSysteme = ({ parameters, state, families, dynamics }) => {
  const initP   = state?.initialParameters || {};
  const active  = families.filter(f => f.isActive);
  const toxic   = families.filter(f => f.isToxic && f.isActive);
  const eman    = families.filter(f => f.isActive && ['Art', 'Médiation'].includes(f.name));

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Familles actives"  value={`${active.length}/${families.length}`} accent="#f59e0b" />
        <Stat label="Toxiques actives"  value={toxic.length}    accent={toxic.length > 0 ? '#f87171' : '#34d399'} />
        <Stat label="Émancipatrices"    value={eman.length}     accent={eman.length > 0 ? '#34d399' : '#94a3b8'} />
        <Stat label="Paramètres"        value={Object.keys(parameters).length} accent="#94a3b8" />
      </div>

      <Divider />
      <SectionHead icon={Activity} label="Métriques finales avec delta" count={Object.keys(parameters).length} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(parameters).map(([key, val]) => {
          const init  = initP[key] ?? 50;
          const delta = val - init;
          const isPos = delta >= 0;
          const dyn   = dynamics?.find(d => d.parameter === key);
          return (
            <div key={key} className="p-4 rounded-xl border border-[rgba(255,255,255,0.06)] bg-white/[0.02] space-y-2">
              <div className="flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[9px] font-bold uppercase text-white/40 truncate">
                    {dyn?.reportLabel || key.replace(/([A-Z])/g, ' $1').trim()}
                  </p>
                  {dyn?.reportDescription && (
                    <p className="text-[7px] text-white/20 italic mt-0.5 leading-tight">{dyn.reportDescription}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <span className="text-2xl font-black tabular-nums text-white">{val}</span>
                  <span className={`ml-1 text-sm font-bold ${isPos ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isPos ? '+' : ''}{delta}
                  </span>
                </div>
              </div>
              <MetricBar value={val} />
              <div className="flex justify-between text-[7px] font-mono text-white/15">
                <span>Départ: {init}</span>
                {dyn?.sensitivityNote && (
                  <span className="italic text-right max-w-[55%] truncate">{dyn.sensitivityNote}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Divider />
      <SectionHead icon={Fingerprint} label="État des familles" count={active.length} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {active.map(f => (
          <div key={f.name} className="p-4 rounded-xl border space-y-2"
            style={{
              borderColor: f.isToxic ? 'rgba(248,113,113,0.25)' : 'rgba(255,255,255,0.06)',
              background:  f.isToxic ? 'rgba(248,113,113,0.04)' : 'rgba(255,255,255,0.01)',
            }}>
            <div className="flex items-center justify-between gap-1">
              <p className="text-[10px] font-black uppercase text-white/70">{f.name}</p>
              {f.isToxic && <Tag color="#f87171">Toxique</Tag>}
            </div>
            {f.narrativeRole && <p className="text-[8px] text-white/30 italic leading-tight">{f.narrativeRole}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const ActionRow = ({ actionId, idx, action, isPivot, da }) => {
  const [open, setOpen] = useState(false);
  const df   = action?.deepFeedback;
  const cc   = action?.causalChain || [];
  const comp = action?.competencySignal;
  const hasDetail = df || cc.length > 0;

  return (
    <div className="rounded-xl border overflow-hidden"
      style={{
        borderColor: isPivot ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.06)',
        background:  isPivot ? 'rgba(245,158,11,0.03)' : 'rgba(255,255,255,0.01)',
      }}>
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors">
        <span className="text-[9px] font-black tabular-nums text-white/20 w-6 shrink-0">
          {String(idx + 1).padStart(2, '0')}
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold truncate ${isPivot ? 'text-amber-300' : 'text-white/75'}`}>
            {action?.label || actionId}
          </p>
          {comp && (
            <p className="text-[8px] font-mono mt-0.5" style={{ color: LEVEL[comp.level]?.color || '#f59e0b' }}>
              ↳ {comp.name} · {comp.level}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isPivot && <Tag color="#f59e0b">Pivot</Tag>}
          {hasDetail && (
            <ChevronRight size={12} className={`text-white/20 transition-transform ${open ? 'rotate-90' : ''}`} />
          )}
        </div>
      </button>

      {open && hasDetail && (
        <div className="border-t border-[rgba(255,255,255,0.06)] p-5 space-y-5">
          {isPivot && da?.pivotExplanation && (
            <div className="px-4 py-3 rounded-lg bg-amber-500/8 border border-amber-500/15">
              <p className="text-[7px] font-black uppercase tracking-widest text-amber-400/50 mb-1.5">Explication du pivot</p>
              <p className="text-xs text-amber-300/80 leading-relaxed">{da.pivotExplanation}</p>
            </div>
          )}
          {df && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { k: 'whatHappened', label: "Ce qui s'est passé", c: '#e2e8f0' },
                { k: 'whyItMatters', label: 'Pourquoi ça compte',  c: '#fbbf24' },
                { k: 'whatItCosts',  label: 'Ce que ça coûte',    c: '#f87171' },
              ].filter(({ k }) => df[k]).map(({ k, label, c }) => (
                <div key={k} className="p-3 rounded-lg bg-black/20 border border-[rgba(255,255,255,0.06)] space-y-1.5">
                  <p className="text-[7px] font-black uppercase tracking-widest" style={{ color: c, opacity: 0.5 }}>{label}</p>
                  <p className="text-[10px] leading-relaxed" style={{ color: c, opacity: 0.85 }}>{df[k]}</p>
                </div>
              ))}
            </div>
          )}
          {df?.boostCondition && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
              <TrendingUp size={9} className="text-emerald-400 mt-0.5 shrink-0" />
              <p className="text-[10px] text-emerald-300/80 italic leading-relaxed">{df.boostCondition}</p>
            </div>
          )}
          {cc.length > 0 && (
            <div className="space-y-2">
              <p className="text-[7px] font-black uppercase tracking-widest text-white/20">Chaîne causale</p>
              {cc.map((link, j) => (
                <div key={j} className="flex items-center gap-2 text-[9px]">
                  <span className="text-white/35 font-mono">{link.from}</span>
                  <span className="text-white/15">→</span>
                  <span className="text-white/55 font-semibold">{link.to}</span>
                  {link.mechanism && <span className="text-white/25 italic">— {link.mechanism}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SectionTrajectoire = ({ interp, completedActions, caseData }) => {
  const da = interp.decisionAnalysis;
  const getAction = (id) => caseData?.steps?.flatMap(s => s.actions || []).find(a => a.id === id);

  return (
    <div className="space-y-10">
      {da?.narrative && (
        <div className="p-6 rounded-xl border border-[rgba(255,255,255,0.06)] bg-white/[0.02]">
          <p className="text-[7px] font-black uppercase tracking-widest text-white/20 mb-3 flex items-center gap-1.5">
            <GitBranch size={8} /> Récit de la trajectoire
          </p>
          <p className="text-base text-white/75 italic leading-relaxed">{da.narrative}</p>
        </div>
      )}
      {da?.earlySignal && (
        <div className="px-5 py-3 rounded-lg border border-amber-500/15 bg-amber-500/5">
          <p className="text-[7px] font-black uppercase tracking-widest text-amber-400/50 mb-1">Signal précoce</p>
          <p className="text-sm text-amber-300/75 leading-relaxed">{da.earlySignal}</p>
        </div>
      )}

      <SectionHead icon={History} label="Séquence décisionnelle" count={completedActions.length} />
      <div className="space-y-2.5">
        {completedActions.length === 0 && (
          <p className="text-center py-8 text-white/20 italic text-sm">Aucune action enregistrée.</p>
        )}
        {completedActions.map((id, i) => (
          <ActionRow key={id} actionId={id} idx={i}
            action={getAction(id)}
            isPivot={id === da?.pivotActionId}
            da={da}
          />
        ))}
      </div>

      {da?.missedOpportunities?.length > 0 && (
        <>
          <Divider />
          <SectionHead icon={AlertTriangle} label="Chemins non pris" count={da.missedOpportunities.length} />
          {da.missedOpportunities.map((opp, i) => (
            <div key={i} className="flex items-start gap-3 px-4 py-3 border border-[rgba(255,255,255,0.06)] rounded-lg bg-white/[0.01] mb-2">
              <span className="text-white/15 text-[9px] font-mono shrink-0 mt-0.5">{i+1}.</span>
              <p className="text-xs text-white/45 leading-relaxed italic">{opp}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const SectionEtapes = ({ caseData, completedActions }) => {
  const steps = caseData?.steps || [];
  const done  = new Set(completedActions);

  if (!steps.length) return <p className="text-center py-8 text-white/20 italic text-sm">Aucune donnée d'étape.</p>;

  return (
    <div className="space-y-5">
      {steps.map((step, si) => {
        const arc  = step.narrativeArc;
        const crit = step.stepSuccessCriteria;
        const keyDone = crit?.keyActionId ? done.has(crit.keyActionId) : true;
        const tension = arc?.tensionLevel ?? 1;

        return (
          <div key={step.id || si} className="rounded-xl border border-[rgba(255,255,255,0.06)] overflow-hidden">
            <div className="flex items-start gap-4 p-5 bg-white/[0.02]">
              <div className="shrink-0 w-7 h-7 rounded-full border border-[rgba(255,255,255,0.06)] flex items-center justify-center">
                <span className="text-[8px] font-black text-white/35">{String(si+1).padStart(2,'0')}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="text-sm font-bold text-white/80">{step.title}</h3>
                  {arc?.position && <Tag color="#94a3b8">{arc.position}</Tag>}
                </div>
                {step.problematisation && (
                  <p className="text-xs text-white/35 italic leading-relaxed">{step.problematisation}</p>
                )}
              </div>
              <div className="flex gap-0.5 shrink-0 mt-1">
                {[1,2,3,4,5].map(t => (
                  <div key={t} className="w-1.5 h-5 rounded-sm"
                    style={{ background: t <= tension
                      ? ['','#94a3b8','#34d399','#f59e0b','#fb923c','#f87171'][t]
                      : 'rgba(255,255,255,0.05)' }} />
                ))}
              </div>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-[rgba(255,255,255,0.06)]">
              {step.learningObjectives?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[7px] font-black uppercase tracking-widest text-white/20">Objectifs</p>
                  <ul className="space-y-1.5">
                    {step.learningObjectives.map((obj, oi) => (
                      <li key={oi} className="flex items-start gap-2 text-[10px] text-white/50 leading-relaxed">
                        <span className="text-white/18 font-mono shrink-0">{String(oi+1).padStart(2,'0')}.</span>
                        {obj}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="space-y-3">
                {arc?.nextStepForeshadow && (
                  <div>
                    <p className="text-[7px] font-black uppercase tracking-widest text-white/20 mb-1">Anticipation</p>
                    <p className="text-[9px] text-white/30 italic leading-relaxed">→ {arc.nextStepForeshadow}</p>
                  </div>
                )}
                {crit && (
                  <div>
                    <p className="text-[7px] font-black uppercase tracking-widest text-white/20 mb-1.5">Critères de réussite</p>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${keyDone ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                      <p className="text-[9px] text-white/45">{keyDone ? 'Action clé accomplie' : 'Action clé manquée'}</p>
                    </div>
                    {!keyDone && crit.feedbackIfMissed && (
                      <p className="text-[8px] text-amber-300/55 italic pl-4 mt-1 leading-tight">{crit.feedbackIfMissed}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SectionAgents = ({ agents }) => {
  const pool = agents.filter(a => a.archetype || a.backstory || a.theory);
  if (!pool.length) return <p className="text-center py-8 text-white/20 italic text-sm">Aucun agent avec profil narratif.</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {pool.map((agent, i) => (
        <div key={agent.name || i} className="p-5 rounded-xl border border-[rgba(255,255,255,0.06)] bg-white/[0.015] space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-white/80">{agent.narrativeName || agent.displayName || agent.name}</p>
              <p className="text-[8px] text-white/30">{agent.role} · {agent.family}</p>
            </div>
            {agent.archetype && <Tag color="#a78bfa">{agent.archetype}</Tag>}
          </div>
          {agent.backstory && (
            <p className="text-xs text-white/45 leading-relaxed italic border-l border-white/8 pl-3">{agent.backstory}</p>
          )}
          {agent.theory?.quote && (
            <div className="space-y-1">
              <p className="text-[7px] font-black uppercase tracking-widest text-white/18">Vecteur théorique</p>
              <p className="text-[9px] text-white/40 italic">"{agent.theory.quote}"</p>
              <p className="text-[7px] text-white/20">— {agent.theory.author} ({agent.theory.year})</p>
            </div>
          )}
          {(agent.vulnerability || agent.resilience) && (
            <div className="flex gap-4">
              {agent.vulnerability && (
                <div className="flex items-center gap-1">
                  <TrendingDown size={8} className="text-red-400/40" />
                  <p className="text-[7px] text-white/20">vulnérabilité: <span className="text-red-400/55">{agent.vulnerability}</span></p>
                </div>
              )}
              {agent.resilience && (
                <div className="flex items-center gap-1">
                  <TrendingUp size={8} className="text-emerald-400/40" />
                  <p className="text-[7px] text-white/20">résilience: <span className="text-emerald-400/55">{agent.resilience}</span></p>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const SectionTheorie = ({ interp, caseData }) => {
  const tfs       = interp.theoreticalFrameworks || [];
  const competing = caseData?.initialState?.theory?.competingFrameworks || [];

  const corpus = useMemo(() => {
    const s = [];
    (caseData?.sources || []).forEach(x => s.push({ ...x, _from: 'cas' }));
    (interp.debriefSources || []).forEach(x => s.push({ ...x, _from: 'solution' }));
    // Aussi scraper les solutions qui ne sont pas la courante
    const seen = new Set();
    return s.filter(x => {
      const k = `${x.title}|${x.author}`;
      if (seen.has(k)) return false;
      seen.add(k); return true;
    });
  }, [caseData, interp]);

  return (
    <div className="space-y-10">
      {tfs.length > 0 && (
        <>
          <SectionHead icon={BookOpen} label="Ancrage théorique" count={tfs.length} />
          <div className="space-y-4">
            {tfs.sort((a,b) => (b.relevanceScore||0) - (a.relevanceScore||0)).map((fw, i) => (
              <div key={i} className="p-6 rounded-xl border space-y-3"
                style={{
                  borderColor: fw.type === 'primary' ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.06)',
                  background:  fw.type === 'primary' ? 'rgba(245,158,11,0.04)' : 'rgba(255,255,255,0.01)',
                }}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-black text-white/80">{fw.author}</p>
                      {fw.type && <Tag color={fw.type === 'primary' ? '#f59e0b' : fw.type === 'tension' ? '#f87171' : '#94a3b8'}>{fw.type}</Tag>}
                    </div>
                    <p className="text-[9px] text-white/35 italic">{fw.concept}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="w-16 h-1 bg-white/8 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400/60 rounded-full" style={{ width: `${(fw.relevanceScore||0)*100}%` }} />
                    </div>
                    <span className="text-[8px] text-white/20 tabular-nums">{Math.round((fw.relevanceScore||0)*100)}%</span>
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">{fw.manifestedAs}</p>
                {fw.quote && (
                  <blockquote className="text-[9px] italic text-white/30 border-l-2 border-white/8 pl-3 leading-relaxed">
                    "{fw.quote}"
                  </blockquote>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {competing.length > 0 && (
        <>
          <Divider />
          <SectionHead icon={GitBranch} label="Frameworks concurrents" count={competing.length} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {competing.map((fw, i) => (
              <div key={i} className="p-4 rounded-lg border border-[rgba(255,255,255,0.06)] bg-white/[0.01] space-y-1">
                <p className="text-xs font-bold text-white/55">{fw.concept}</p>
                <p className="text-[8px] text-white/28 italic">{fw.author}{fw.year ? `, ${fw.year}` : ''}</p>
                {fw.keyDifference && <p className="text-[8px] text-white/20 leading-relaxed">{fw.keyDifference}</p>}
              </div>
            ))}
          </div>
        </>
      )}

      {corpus.length > 0 && (
        <>
          <Divider />
          <SectionHead icon={FileText} label="Corpus de référence" count={corpus.length} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {corpus.map((src, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg border border-[rgba(255,255,255,0.06)] bg-white/[0.01] hover:border-white/10 transition-colors">
                <span className="text-[7px] font-mono text-white/15 w-5 shrink-0 text-right mt-0.5">[{i+1}]</span>
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-xs font-bold text-white/65 truncate">{src.title}</p>
                  <p className="text-[8px] text-white/30 italic">{src.author}{src.year ? `, ${src.year}` : ''}</p>
                  {src.keyPassage && <p className="text-[7px] text-white/18 italic leading-tight">{src.keyPassage}</p>}
                </div>
                {src.url && (
                  <a href={src.url} target="_blank" rel="noreferrer"
                    className="text-white/18 hover:text-amber-400 transition-colors shrink-0 mt-0.5">
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SectionCompetences = ({ interp, completedActions, caseData }) => {
  const comps = interp.competencies || [];
  const getAction = (id) => caseData?.steps?.flatMap(s => s.actions || []).find(a => a.id === id);

  return (
    <div className="space-y-10">
      {comps.length > 0 ? (
        <>
          <SectionHead icon={Target} label="Compétences démontrées" count={comps.length} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {comps.map((comp, i) => {
              const lv = LEVEL[comp.level] || LEVEL.intermédiaire;
              return (
                <div key={i} className="p-5 rounded-xl border space-y-3"
                  style={{ borderColor: `${lv.color}25`, background: lv.bg }}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-black" style={{ color: lv.color }}>{comp.name}</p>
                    <span className="text-[7px] font-black uppercase px-2 py-0.5 rounded border"
                      style={{ color: lv.color, borderColor: `${lv.color}35` }}>
                      {lv.label}
                    </span>
                  </div>
                  {comp.evidence && <p className="text-xs text-white/50 leading-relaxed">{comp.evidence}</p>}
                  {comp.descriptors?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {comp.descriptors.map((d, di) => (
                        <span key={di} className="text-[7px] px-2 py-0.5 rounded-full bg-white/5 border border-[rgba(255,255,255,0.06)] text-white/35">{d}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : <p className="text-center py-8 text-white/20 italic text-sm">Aucune compétence mappée pour cette solution.</p>}

      {completedActions.length > 0 && (
        <>
          <Divider />
          <SectionHead icon={Zap} label="Signaux par action" />
          <div className="space-y-1.5">
            {completedActions.map((id, i) => {
              const action = getAction(id);
              const sig    = action?.competencySignal;
              if (!sig) return null;
              const lv = LEVEL[sig.level] || LEVEL.intermédiaire;
              return (
                <div key={id} className="flex items-center gap-4 p-3 rounded-lg border border-[rgba(255,255,255,0.06)] bg-white/[0.01]">
                  <span className="text-[7px] font-mono text-white/15 w-6 text-right">{String(i+1).padStart(2,'0')}</span>
                  <p className="flex-1 text-xs text-white/50 truncate">{action?.label || id}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="text-[9px] font-semibold" style={{ color: lv.color }}>{sig.name}</p>
                    <span className="text-[7px] px-1.5 py-0.5 rounded border font-black uppercase"
                      style={{ color: lv.color, borderColor: `${lv.color}35` }}>{lv.label}</span>
                  </div>
                </div>
              );
            }).filter(Boolean)}
          </div>
        </>
      )}
    </div>
  );
};

const SectionTransfert = ({ interp }) => {
  const t    = interp.transferability || {};
  const recs = interp.recommendations  || [];

  return (
    <div className="space-y-10">
      {(t.toSchool || t.toEnterprise || t.toPolicy) && (
        <>
          <SectionHead icon={Map} label="Transférabilité" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: 'toSchool',     label: 'École / Formation',         accent: '#60a5fa' },
              { key: 'toEnterprise', label: 'Organisation / Entreprise', accent: '#a78bfa' },
              { key: 'toPolicy',     label: 'Politique publique',        accent: '#34d399' },
            ].filter(({ key }) => t[key]).map(({ key, label, accent }) => (
              <div key={key} className="p-5 rounded-xl border border-[rgba(255,255,255,0.06)] bg-white/[0.01] space-y-3">
                <Tag color={accent}>{label}</Tag>
                <p className="text-xs text-white/60 leading-relaxed">{t[key]}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {t.realWorldLevers?.length > 0 && (
        <div className="p-6 rounded-xl border border-emerald-500/15 bg-emerald-500/5 space-y-4">
          <SectionHead icon={TrendingUp} label="Leviers concrets" count={t.realWorldLevers.length} />
          <div className="space-y-2.5">
            {t.realWorldLevers.map((lever, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-emerald-400/45 shrink-0 leading-tight">→</span>
                <p className="text-xs text-white/60 leading-relaxed">{lever}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {recs.length > 0 && (
        <>
          <Divider />
          <SectionHead icon={FileText} label="Plan d'action recommandé" count={recs.length} />
          <div className="space-y-3">
            {recs.sort((a,b) => a.priority - b.priority).map((rec, i) => (
              <div key={i} className="flex items-start gap-5 p-5 rounded-xl border border-[rgba(255,255,255,0.06)] bg-white/[0.01] hover:border-white/10 transition-colors">
                <span className="text-3xl font-black shrink-0 tabular-nums leading-none mt-1"
                  style={{ color: PRIORITY_ACCENT[rec.priority] || '#94a3b8' }}>
                  {String(rec.priority).padStart(2, '0')}
                </span>
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-white/75">{rec.action}</p>
                    <Tag color="#94a3b8">{rec.timeframe}</Tag>
                    {rec.actor && <Tag color="#60a5fa">{rec.actor}</Tag>}
                  </div>
                  {rec.rationale && <p className="text-xs text-white/40 italic leading-relaxed">{rec.rationale}</p>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SectionDebat = ({ interp }) => {
  const qs = interp.openQuestions      || [];
  const ex = interp.historicalExamples || [];

  return (
    <div className="space-y-10">
      {qs.length > 0 && (
        <>
          <SectionHead icon={MessageSquare} label="Questions ouvertes" count={qs.length} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {qs.map((q, i) => (
              <div key={i}
                className="group p-5 rounded-xl border border-[rgba(255,255,255,0.06)] hover:border-amber-500/20 hover:bg-amber-500/[0.03] transition-all">
                <div className="flex items-start gap-3">
                  <span className="text-2xl font-black shrink-0 leading-none mt-0.5 group-hover:scale-110 transition-transform text-amber-500/60">?</span>
                  <p className="text-sm text-white/65 italic leading-relaxed">{q}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {ex.length > 0 && (
        <>
          <Divider />
          <SectionHead icon={History} label="Corrélations historiques" count={ex.length} />
          <div className="space-y-3">
            {ex.map((e, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-xl border border-[rgba(255,255,255,0.06)] hover:border-amber-500/15 transition-colors">
                <span className="px-2 py-1 rounded border border-amber-500/20 bg-amber-500/8 text-amber-400/70 text-[7px] font-black shrink-0">
                  REF_{i+1}
                </span>
                <p className="text-sm text-white/60 leading-relaxed">{e}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {!qs.length && !ex.length && (
        <p className="text-center py-8 text-white/20 italic text-sm">Aucun contenu de débat disponible.</p>
      )}
    </div>
  );
};

// RAPPORT PRINCIPAL

const RapportEnquete = ({ onClose }) => {
  const { engine } = usePesa();
  const [activeId, setActiveId] = useState('synthese');
  const contentRef = useRef(null);
  const reportID   = useMemo(() => `PESACORE-${Math.random().toString(36).substr(2,6).toUpperCase()}`, []);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null); // null | 'pdf' | 'pptx'

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeId]);

  if (!engine) return null;

  const { state, case: caseData } = engine;
  const sol              = state?.currentSolution || {};
  const interp           = sol.interpretation    || {};
  const parameters       = state?.parameters     || {};
  const families         = state?.families        || [];
  const agents           = state?.agents          || [];
  const completedActions = Array.from(state?.completedActions || []);
  const dynamics         = caseData?.simulationEngine?.dynamics || [];
  const tone             = TONE[interp.tone] || TONE.lucide;

  const SP = { sol, interp, tone, caseData, parameters, state, families, agents, completedActions, dynamics };

  const renderSection = (id) => {
    switch (id) {
      case 'synthese':    return <SectionSynthese    {...SP} />;
      case 'systeme':     return <SectionSysteme     {...SP} />;
      case 'trajectoire': return <SectionTrajectoire {...SP} />;
      case 'etapes':      return <SectionEtapes      {...SP} />;
      case 'agents':      return <SectionAgents      {...SP} />;
      case 'theorie':     return <SectionTheorie     {...SP} />;
      case 'competences': return <SectionCompetences {...SP} />;
      case 'transfert':   return <SectionTransfert   {...SP} />;
      case 'debat':       return <SectionDebat       {...SP} />;
      default:            return null;
    }
  };

  const curIdx  = SECTIONS.findIndex(s => s.id === activeId);
  const hasPrev = curIdx > 0;
  const hasNext = curIdx < SECTIONS.length - 1;

  const handleExportPdf = () => {
    exportPdf(engine);
  };

  const handleExportPptx = async () => {
    setExporting(true);
    setExportStatus('pptx');
    try {
      await exportPptx(engine);
    } finally {
      setExporting(false);
      setExportStatus(null);
    }
  };

  if (!sol.interpretation && state?.isPhaseRunning === false) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: '#080808', zIndex: 99999 }}>
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-8 h-8 border-2 border-white/10 border-t-amber-500/50 rounded-full mx-auto mb-4"
          />
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/20">
            Calcul des résultats…
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── PRINT STYLES ──────────────────────────────────────────────── */}      
      <div id="pesa-rapport-root" className="fixed inset-0" style={{ zIndex: 99999 }}>        

        {/* ── INTERFACE ÉCRAN ───────────────────────────────────────────── */}
        <div id="rapport-screen" className="flex w-full h-full" style={{ background: '#080808' }}>

          {/* Sidebar */}
          <aside className="shrink-0 w-52 flex flex-col border-r border-[rgba(255,255,255,0.04)]" style={{ background: '#13131a' }}>  {/* ← plus lumineux */}
            {/* Header */}
            <div className="px-5 py-5 border-b border-[rgba(255,255,255,0.04)] space-y-1">  {/* ← 0.06 → 0.04 */}
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-white/20">Rapport d'Enquête</p>
              <p className="text-[9px] font-mono text-white/12">{reportID}</p>
            </div>
            
            {/* Solution */}
            {sol.name && (
              <div className="px-5 py-4 border-b border-[rgba(255,255,255,0.04)] space-y-2">  {/* ← 0.06 → 0.04 */}
                <p className="text-[9px] uppercase tracking-widest text-white/18">Résultat</p>
                <div className="flex items-center gap-1.5">
                  <tone.Icon size={10} style={{ color: tone.accent }} />
                  <p className="text-[11px] font-black" style={{ color: tone.accent }}>{tone.label}</p>
                </div>
                <p className="text-[11px] text-white/45 leading-tight font-semibold">{sol.name}</p>
                {interp.theoreticalPromise != null && interp.realWorldProbability != null && (
                  <div className="pt-1 space-y-1">
                    <div className="flex justify-between text-[9px] text-white/20">
                      <span>Théorique</span><span>{interp.theoreticalPromise}%</span>
                    </div>
                    <div className="flex justify-between text-[9px]" style={{ color: tone.accent }}>
                      <span>Réel</span><span>{interp.realWorldProbability}%</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
              {SECTIONS.map(s => {
                const active = s.id === activeId;
                return (
                  <button key={s.id} onClick={() => setActiveId(s.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all"
                    style={{
                      background:  active ? `${tone.accent}10` : 'transparent',
                      borderLeft:  active ? `2px solid ${tone.accent}` : '2px solid transparent',
                      color:       active ? tone.accent : 'rgba(255,255,255,0.3)',
                    }}>
                    <s.Icon size={12} />
                    <span className="text-[11px] font-bold uppercase tracking-wide leading-tight">{s.label}</span>
                  </button>
                );
              })}
            </nav>
            
            {/* Footer sidebar */}
            <div className="px-4 py-4 border-t border-[rgba(255,255,255,0.04)] space-y-2">  {/* ← remplacer border-amber/5 */}
              <button
                onClick={handleExportPdf}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[rgba(255,255,255,0.06)] text-white/30 hover:text-white/60 hover:border-[rgba(255,255,255,0.12)] transition-all text-[10px] font-black uppercase tracking-widest"
              >
                <Download size={10} /> PDF imprimable
              </button>
              <button
                onClick={handleExportPptx}
                disabled={exporting}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[rgba(255,255,255,0.06)] text-white/30 hover:text-amber-400 hover:border-amber-400/20 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-40 disabled:cursor-wait"
              >
                {exporting && exportStatus === 'pptx' ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    Génération…
                  </>
                ) : (
                  <>
                    <Presentation size={10} /> Export PPTX
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[rgba(239,68,68,0.15)] text-red-400/40 hover:bg-red-500/8 hover:text-red-400 transition-all text-[8px] font-black uppercase tracking-widest" 
              >
                <X size={10} /> Fermer
              </button>
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Content header */}
            <div className="shrink-0 px-8 py-3.5 border-b border-[rgba(255,255,255,0.06)] flex items-center justify-between"
              style={{ background: '#0c0c0c' }}>
              {(() => {
                const s = SECTIONS.find(x => x.id === activeId);
                return s ? (
                  <div className="flex items-center gap-2">
                    <s.Icon size={13} style={{ color: tone.accent }} />
                    <h2 className="text-xs font-black uppercase tracking-[0.2em]" style={{ color: tone.accent }}>{s.label}</h2>
                  </div>
                ) : null;
              })()}
              <div className="flex items-center gap-2">
                <span className="text-[7px] font-mono text-white/12 hidden md:block">
                  {caseData?.slug?.toUpperCase()} · {new Date().toLocaleDateString('fr-FR')}
                </span>
                <button
                  onClick={handleExportPdf}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[rgba(255,255,255,0.06)] text-white/25 hover:text-white/55 hover:border-white/15 transition-all text-[7px] font-black uppercase tracking-widest"
                >
                  <Download size={9} /> PDF
                </button>
                <button
                  onClick={handleExportPptx}
                  disabled={exporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[rgba(255,255,255,0.06)] text-white/25 hover:text-amber-400/70 hover:border-amber-400/20 transition-all text-[7px] font-black uppercase tracking-widest disabled:opacity-40"
                >
                  <Presentation size={9} /> PPTX
                </button>
                <button onClick={onClose} className="p-1.5 rounded border border-red-500/12 text-red-400/35 hover:bg-red-500/8 hover:text-red-400 transition-all">
                  <X size={13} />
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div ref={contentRef} className="flex-1 overflow-y-auto"
              style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.05) transparent' }}>
              <div className="max-w-4xl mx-auto px-8 py-10">
                <AnimatePresence mode="wait">
                  <motion.div key={activeId}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}>
                    {renderSection(activeId)}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Footer nav */}
            <div className="shrink-0 px-8 py-3 border-t border-[rgba(255,255,255,0.06)] flex items-center justify-between"
              style={{ background: '#0c0c0c' }}>
              <button onClick={() => hasPrev && setActiveId(SECTIONS[curIdx - 1].id)}
                className="text-[8px] font-black uppercase tracking-widest transition-all"
                style={{ color: hasPrev ? 'rgba(255,255,255,0.25)' : 'transparent', cursor: hasPrev ? 'pointer' : 'default' }}>
                ← Précédent
              </button>
              <div className="flex gap-1.5">
                {SECTIONS.map(s => (
                  <button key={s.id} onClick={() => setActiveId(s.id)}
                    className="w-1.5 h-1.5 rounded-full transition-all"
                    style={{ background: s.id === activeId ? tone.accent : 'rgba(255,255,255,0.08)' }} />
                ))}
              </div>
              <button onClick={() => hasNext && setActiveId(SECTIONS[curIdx + 1].id)}
                className="text-[8px] font-black uppercase tracking-widest transition-all"
                style={{ color: hasNext ? 'rgba(255,255,255,0.25)' : 'transparent', cursor: hasNext ? 'pointer' : 'default' }}>
                Suivant →
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RapportEnquete;